import {
  CLIENT_ACTION_NOTE_MAX,
  CLIENT_ACTION_STATUSES,
  CLIENT_ACTIONS,
  clientActionCreateSchema,
  clientActionUpdateSchema,
  type InvestorClientAction,
} from '@fruuit/shared'
import { Button, Callout, Dialog, DialogCancel, Select, Textarea, useToast } from '@fruuit/ui'
import { type FormEvent, useState } from 'react'
import { api } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { type FieldErrors, serverErrors, validate } from '../engagements/forms'
import { LANE_D_KEYS } from '../engagements/queries'

export interface CompanyChoice {
  issuerId: string
  name: string
  engagementId?: string | null
}

export type ClientActionDialogProps = {
  open: boolean
  onClose: () => void
} & (
  | { mode: 'create'; companies: CompanyChoice[] }
  | { mode: 'edit'; action: InvestorClientAction }
)

/**
 * Record or edit a client action (PORT-013/-015). It is the investor's own note for
 * discussion: it never changes Fruuit's finding and executes nothing.
 */
export function ClientActionDialog(props: ClientActionDialogProps) {
  const editing = props.mode === 'edit' ? props.action : null
  const companies = props.mode === 'create' ? props.companies : []
  const [company, setCompany] = useState(companies[0]?.issuerId ?? '')
  const [action, setAction] = useState<string>(editing?.action ?? CLIENT_ACTIONS.values[0])
  const [status, setStatus] = useState<string>(editing?.status ?? 'Recorded for discussion')
  const [note, setNote] = useState(editing?.note ?? '')
  const [errors, setErrors] = useState<FieldErrors>({})
  const { toast } = useToast()

  function close() {
    setErrors({})
    props.onClose()
  }

  const mutation = useApiMutation(
    (input: Record<string, unknown>, { requestId }) =>
      editing
        ? api.patch(`/client-actions/${editing.id}`, input, { requestId })
        : api.post('/client-actions', input, { requestId }),
    {
      invalidate: LANE_D_KEYS,
      onSuccess: () => {
        close()
        toast({
          tone: 'success',
          message: editing ? 'Client action updated.' : 'Client action recorded.',
        })
      },
      onError: (error) => setErrors(serverErrors(error).fields),
    },
  )
  const formError = mutation.error ? serverErrors(mutation.error).message : null

  function submit(event: FormEvent) {
    event.preventDefault()
    const chosen = companies.find((c) => c.issuerId === (company || companies[0]?.issuerId))
    const checked = editing
      ? validate(clientActionUpdateSchema, { revision: editing.revision, action, note, status })
      : validate(clientActionCreateSchema, {
          issuer_id: chosen?.issuerId ?? '',
          ...(chosen?.engagementId ? { engagement_id: chosen.engagementId } : {}),
          action,
          note,
        })
    if (!checked.ok) {
      setErrors(checked.errors)
      return
    }
    setErrors({})
    mutation.mutate(checked.data as Record<string, unknown>)
  }

  return (
    <Dialog
      open={props.open}
      onClose={close}
      title={editing ? 'Edit client action' : 'Record client action'}
      description="Private to your organisation. It does not change Fruuit's research finding or execute a portfolio instruction."
      footer={
        <>
          <DialogCancel />
          <Button type="submit" form="client-action-form" loading={mutation.isPending}>
            {editing ? 'Save changes' : 'Record action'}
          </Button>
        </>
      }
    >
      <form id="client-action-form" onSubmit={submit} noValidate className="space-y-4">
        {editing ? (
          <p className="text-sm text-ink-soft">
            <span className="font-semibold text-ink">{editing.issuer.name}</span>
            {editing.engagement ? ` · ${editing.engagement.title}` : ''}
          </p>
        ) : (
          <Select
            label="Company"
            required
            options={companies.map((c) => ({ value: c.issuerId, label: c.name }))}
            value={company || companies[0]?.issuerId || ''}
            onChange={(event) => setCompany(event.target.value)}
            error={errors.issuer_id}
          />
        )}
        <Select
          label="Action"
          required
          options={CLIENT_ACTIONS.values.map((value) => ({ value, label: value }))}
          value={action}
          onChange={(event) => setAction(event.target.value)}
          error={errors.action}
        />
        {editing ? (
          <Select
            label="Status"
            options={CLIENT_ACTION_STATUSES.values.map((value) => ({ value, label: value }))}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            error={errors.status}
          />
        ) : null}
        <Textarea
          label="Note"
          hint="Why, and what you would like to discuss."
          required
          rows={4}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={CLIENT_ACTION_NOTE_MAX}
          error={errors.note}
        />
        {formError ? (
          <Callout tone="danger" role="alert">
            {formError}
          </Callout>
        ) : null}
      </form>
    </Dialog>
  )
}
